import React, { useContext, useEffect, useState } from 'react';
import { PlusCircle, Trash2, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { PostCallWithErrorResponse, simpleGetCallWithErrorResponse } from '../../api/ApiServices';
import ApiConfig from '../../api/ApiConfig';
import { ToastMsg } from '../ChatAdmin/toastmsg';
import { formatCurrency } from './AccountReportShared';

const ManualLedgerFormPage = () => {
  const { sidebar } = useContext(AppContext);

  const initialRows = [
    { LedgerId: '', debit: '', credit: '', LineMemo: '' },
    { LedgerId: '', debit: '', credit: '', LineMemo: '' },
  ];
  const initialHeader = {
    PostingDate: new Date().toISOString().split('T')[0],
    ReferenceNo: '',
    Description: '',
  };

  const [rows, setRows] = useState(initialRows);
  const [header, setHeader] = useState(initialHeader);
  const [isValid, setIsValid] = useState(true);
  const [lederList, setLedgerList] = useState([]);

  const totalDebit = rows.reduce((sum, r) => sum + (parseFloat(r.debit) || 0), 0);
  const totalCredit = rows.reduce((sum, r) => sum + (parseFloat(r.credit) || 0), 0);
  const difference = totalDebit - totalCredit;
  const isBalanced = Math.abs(difference) < 0.01 && totalDebit > 0;

  const getLedgerList = () => {
    simpleGetCallWithErrorResponse(ApiConfig.GET_LEDGER_LIST).then((res) => {
      if (res?.json?.Success) setLedgerList(res?.json?.Data);
    });
  };

  useEffect(() => {
    getLedgerList();
  }, []);

  const addRow = () => setRows([...rows, { LedgerId: '', debit: '', credit: '', LineMemo: '' }]);

  const removeRow = (id) => {
    if (rows.length > 2) setRows(rows.filter((r) => r.id !== id));
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((r, idx) => (idx === index ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = () => {
    let valid = true;
    if (!header?.ReferenceNo || !header?.Description) valid = false;
    rows?.forEach((i) => { if (!i?.LedgerId || !i?.LineMemo?.length) valid = false; });
    setIsValid(valid);

    if (valid) {
      PostCallWithErrorResponse(ApiConfig.CREATE_MANUAL_LEDGER, JSON.stringify({ ...header, Lines: rows })).then((res) => {
        if (res?.json?.Success) {
          setRows(initialRows);
          setHeader(initialHeader);
          ToastMsg('success', res?.json?.Message);
        } else {
          ToastMsg('error', res?.json?.Message);
        }
      });
    }
  };

  return (
    <main className={sidebar ? "taskMain" : "cx-active taskMain"} id="cx-main">
      <div id="sales-main">
        <section className="cx-inneer-header">
          <div className="main-inner">
            <div className="left">
              <label>Manual Ledger Form</label>
            </div>
          </div>
        </section>

        <section className="mt-3">
          <div className="card border p-4" style={{ borderRadius: '12px' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
              <div>
                <h4 className="mb-1">Create Manual Journal Entry</h4>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Record adjustments, transfers, or non-system transactions.</p>
              </div>
              <div className="text-end">
                <span className="badge bg-secondary">Draft Status</span>
                <span className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>JE #{new Date().getFullYear()}-0042</span>
              </div>
            </div>

            {/* Form Metadata */}
            <div className="row g-3 mb-4 p-3 rounded-3 bg-light">
              <div className="col-md-3">
                <label className="form-label fw-bold text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>Posting Date</label>
                <input type="date" className="form-control form-control-sm" value={header.PostingDate} onChange={(e) => setHeader({ ...header, PostingDate: e.target.value })} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-bold text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>Reference / Slip #</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="e.g. ADJ-OCT-01"
                  value={header.ReferenceNo}
                  onChange={(e) => setHeader({ ...header, ReferenceNo: e.target.value })}
                  style={{ borderColor: !isValid && !header.ReferenceNo ? 'red' : '' }}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>Journal Description</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Reason for this entry..."
                  value={header.Description}
                  onChange={(e) => setHeader({ ...header, Description: e.target.value })}
                  style={{ borderColor: !isValid && !header.Description ? 'red' : '' }}
                />
              </div>
            </div>

            {/* Ledger Table */}
            <div className="table-responsive mb-3">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr className="text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>
                    <th style={{ width: '35%' }}>Account</th>
                    <th style={{ width: '15%' }}>Debit</th>
                    <th style={{ width: '15%' }}>Credit</th>
                    <th>Line Memo</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={index}>
                      <td className="p-2">
                        <select
                          className={`form-select form-select-sm ${!isValid && !row.LedgerId ? 'border-danger' : ''}`}
                          value={row.LedgerId}
                          onChange={(e) => updateRow(index, 'LedgerId', Number(e.target.value))}
                        >
                          <option value="">Select Account...</option>
                          {lederList?.map((acc) => <option key={acc.id} value={acc.Id}>{acc.Name}</option>)}
                        </select>
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="0.00"
                          value={row.debit}
                          onChange={(e) => { updateRow(index, 'debit', Number(e.target.value) || ''); updateRow(index, 'credit', 0); }}
                          disabled={row.credit > 0}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="0.00"
                          value={row.credit}
                          onChange={(e) => { updateRow(index, 'credit', Number(e.target.value) || ''); updateRow(index, 'debit', 0); }}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          className={`form-control form-control-sm ${!isValid && !row.LineMemo?.length ? 'border-danger' : ''}`}
                          placeholder="Line description..."
                          value={row.LineMemo}
                          onChange={(e) => updateRow(index, 'LineMemo', e.target.value)}
                        />
                      </td>
                      <td className="text-center">
                        <button className="btn btn-link text-danger p-1" onClick={() => removeRow(row.id)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="fw-bold" style={{ backgroundColor: isBalanced ? '#f0fff4' : '#fff5f5' }}>
                  <tr>
                    <td className="text-end py-3">Totals</td>
                    <td className="py-3">{formatCurrency(totalDebit)}</td>
                    <td className="py-3">{formatCurrency(totalCredit)}</td>
                    <td colSpan="2" className={`text-end py-3 ${isBalanced ? 'text-success' : 'text-danger'}`}>
                      {isBalanced
                        ? <span><CheckCircle size={14} className="me-1" />Balanced</span>
                        : <span><AlertCircle size={14} className="me-1" />Out of Balance: {formatCurrency(difference)}</span>}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Actions */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={addRow}>
                <PlusCircle size={16} /> Add Line
              </button>
              <div className="d-flex gap-2">
                <button className="btn btn-light">Cancel</button>
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  disabled={!isBalanced}
                  onClick={handleSubmit}
                >
                  <Save size={16} /> Post Journal
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ManualLedgerFormPage;
